import styled from '@emotion/styled';
import {Col, Container, Row, Spacer} from '@nfq/react-grid';

import {TopBarLink} from 'UI/components/action/TopBarLink';

import {useTopBar} from './useTopBar';
import {IconMail, IconPhone, Logo} from 'Images/icons';

import type {Header} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the properties for the `TopBar` component.
 * This interface specifies the required header data configuration and optional testing identifier needed to render the top navigation bar.
 * The interface ensures type safety for the component's props and provides clear documentation for developers using this component.
 */
interface ComponentProps {
    /**
     * The header configuration data containing contact information, branding details, and navigation links.
     * This data structure includes phone numbers, email addresses, company links, and other header-related content that will be displayed in the top bar.
     */
    headerData: Header;
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing scenarios and automated test suites.
     * The identifier helps ensure reliable and accurate component targeting in both unit and integration tests.
     *
     * @default 'TopBar'
     */
    testId?: string;
}

/**
 * The TopBar component renders a sticky header navigation bar containing contact information and branding links.
 * It displays phone, email, and company logo links in a horizontal layout using the TopBarLink component for each item.
 * This component serves as the primary navigation element positioned at the top of the page layout.
 *
 * @param props            The component props.
 * @param props.headerData The header configuration data containing contact information and link details.
 * @param props.testId     Test identifier for component testing purposes.
 * @returns The TopBar component JSX structure with contact links and branding.
 *
 * @example
 * ```tsx
 * <TopBar
 *     headerData={headerConfig}
 *     testId="main-top-bar"
 * />
 * ```
 */
const TopBar = ({headerData, testId = 'TopBar'}: ComponentProps) => {
    const {linkHref, linkLabel, mailHref, mailLabel, phoneHref, phoneLabel} = useTopBar(headerData);

    return (
        <Wrapper data-cy={testId}>
            <Container>
                <Row align="center">
                    <Col direction="row">
                        <TopBarLink href={phoneHref} icon={IconPhone} label={phoneLabel} />
                        <Spacer maxX={{md: 30, xs: 30}} x={4} />
                        <TopBarLink href={mailHref} icon={IconMail} label={mailLabel} />
                        <Spacer maxX={{md: 30, xs: 30}} x={4} />
                        <TopBarLink href={linkHref} icon={Logo} label={linkLabel} target="_blank" />
                    </Col>
                </Row>
            </Container>
        </Wrapper>
    );
};

TopBar.displayName = 'TopBar';

export {TopBar};

const Wrapper = styled.div`
    align-items: center;
    background-color: ${({theme}) => theme.colors.tertiary};
    display: flex;
    height: 4rem;
    justify-content: space-between;
`;